// Object Swap modal — "Change shape" / "Change icon" (owner directive §3,
// fabric6 SW lane). Swap the selected object's SILHOUETTE while KEEPING all its
// style customizations — the swap engine (useCanvasEditor.swapShapeType /
// swapIcon) carries fill / stroke / outline / effects / 3D / the whole data
// bag; this modal is ONLY the picker. Portaled to document.body and joined to
// the shared Esc stack exactly like TextureExplorerModal (Esc closes the
// top-most modal only, never the editor's deselect hotkey). Self-contained: its
// own namespaced stylesheet, no shared CSS touched.
import { API_BASE } from '../../../config.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { pushEscHandler, popEscHandler } from './inspector/text/StylesExplorerModal';
import './ObjectSwapModal.css';

// Educator-named, clean SVG previews for the swappable shape silhouettes. The
// `type` ids match the engine's factory (buildSwapShape → swapShapeType) 1:1 so
// a pick builds the matching plain fabric primitive. (The left panel's ShapeIcon
// lives in StudioCoreEditor and isn't importable — these are the same outlines
// drawn clean, at a shared 40×40 viewBox.)
const SHAPE_SWATCHES = [
  { type: 'rectangle', label: 'Rectangle', el: <rect x="4" y="8" width="32" height="24" /> },
  { type: 'softRect', label: 'Rounded', el: <rect x="4" y="8" width="32" height="24" rx="6" /> },
  { type: 'circle', label: 'Circle', el: <circle cx="20" cy="20" r="16" /> },
  { type: 'ellipse', label: 'Oval', el: <ellipse cx="20" cy="20" rx="18" ry="12" /> },
  { type: 'triangle', label: 'Triangle', el: <polygon points="20,4 36,36 4,36" /> },
  { type: 'invTriangle', label: 'Inverted triangle', el: <polygon points="4,4 36,4 20,36" /> },
  { type: 'diamond', label: 'Diamond', el: <polygon points="20,2 38,20 20,38 2,20" /> },
  { type: 'parallelogram', label: 'Parallelogram', el: <polygon points="10,8 38,8 30,32 2,32" /> },
  { type: 'trapezoid', label: 'Trapezoid', el: <polygon points="8,8 32,8 38,32 2,32" /> },
  { type: 'pentagon', label: 'Pentagon', el: <polygon points="20,2 38,14 32,36 8,36 2,14" /> },
  { type: 'hexagon', label: 'Hexagon', el: <polygon points="10,4 30,4 38,20 30,36 10,36 2,20" /> },
  { type: 'octagon', label: 'Octagon', el: <polygon points="12,2 28,2 38,12 38,28 28,38 12,38 2,28 2,12" /> },
  { type: 'star4', label: '4-point star', el: <polygon points="20,2 24,16 38,20 24,24 20,38 16,24 2,20 16,16" /> },
  { type: 'star', label: '5-point star', el: <polygon points="20,2 24,14 38,14 27,23 31,36 20,28 9,36 13,23 2,14 16,14" /> },
  { type: 'star6', label: '6-point star', el: <polygon points="20,2 25,14 38,14 28,20 38,26 25,26 20,38 15,26 2,26 12,20 2,14 15,14" /> },
  { type: 'star8', label: '8-point star', el: <polygon points="20,2 23,12 30,5 27,15 38,14 29,20 38,26 27,25 30,35 23,28 20,38 17,28 10,35 13,25 2,26 11,20 2,14 13,15 10,5 17,12" /> },
  { type: 'heart', label: 'Heart', el: <path d="M20,36 C10,28 2,20 2,12 C2,6 8,2 14,2 C17,2 19,4 20,6 C21,4 23,2 26,2 C32,2 38,6 38,12 C38,20 30,28 20,36 Z" /> },
  { type: 'cross', label: 'Plus', el: <polygon points="14,2 26,2 26,14 38,14 38,26 26,26 26,38 14,38 14,26 2,26 2,14 14,14" /> },
  { type: 'checkmark', label: 'Check mark', el: <path d="M6,22 L14,30 L34,10 L30,6 L14,22 L10,18 Z" /> },
  { type: 'xmark', label: 'X mark', el: <path d="M10,6 L20,16 L30,6 L34,10 L24,20 L34,30 L30,34 L20,24 L10,34 L6,30 L16,20 L6,10 Z" /> },
  { type: 'cloud', label: 'Cloud', el: <path d="M10,30 C4,30 2,26 4,22 C2,18 4,14 10,14 C12,10 18,10 22,12 C24,8 32,10 32,16 C36,16 38,20 36,24 C38,28 34,30 30,30 Z" /> },
  { type: 'lightning', label: 'Lightning', el: <polygon points="24,2 14,18 20,18 12,38 28,16 22,16 28,2" /> },
  { type: 'crescent', label: 'Crescent', el: <path d="M20,2 C32,2 38,12 38,22 C38,32 28,38 18,38 C24,34 26,26 26,20 C26,14 24,6 18,2 Z" /> },
  { type: 'speechBubble', label: 'Speech bubble', el: <path d="M4,6 h28 a4,4 0 0 1 4,4 v16 a4,4 0 0 1 -4,4 h-18 l-6,8 v-8 h-4 a4,4 0 0 1 -4,-4 v-16 a4,4 0 0 1 4,-4 z" /> },
  { type: 'arrow', label: 'Arrow', el: <polygon points="2,15 26,15 26,8 38,20 26,32 26,25 2,25" /> },
  { type: 'doubleArrow', label: 'Double arrow', el: <polygon points="2,20 12,10 12,16 28,16 28,10 38,20 28,30 28,24 12,24 12,30" /> },
];

// Icon browser preview tint — a light slate so silhouettes read on the dark
// modal (the REAL tint is the object's own, applied by swapIcon; the picker
// only needs the shapes legible). The iconify search + thumbnail URLs mirror
// the left Elements panel's icon tab verbatim (one source of truth for the API).
const ICON_PREVIEW_COLOR = '#cbd5e1';
const ICON_QUICK_TAGS = ['home', 'star', 'heart', 'arrow', 'check', 'user', 'settings', 'search'];

export default function ObjectSwapModal({ mode = 'shape', activeIcon = null, onPick, onClose }) {
  const isIcon = mode === 'icon';

  // Esc closes ONLY this modal, and only while it is the top-most one (shared
  // stack — see StylesExplorerModal). onClose rides a ref so the handler binds
  // once per mount.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const handler = () => closeRef.current?.();
    pushEscHandler(handler);
    return () => popEscHandler(handler);
  }, []);

  // ---- icon browser state (only meaningful in icon mode) ----
  const [query, setQuery] = useState('');
  const [icons, setIcons] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const runSearch = async (term) => {
    const q = (term ?? query).trim();
    if (!q) return;
    setIsSearching(true);
    setSearched(true);
    try {
      const res = await fetch(
        `${API_BASE}/design/icons/search?query=${encodeURIComponent(q)}&limit=48`,
      );
      const data = await res.json();
      setIcons(Array.isArray(data.icons) ? data.icons : []);
    } catch {
      setIcons([]);
    } finally {
      setIsSearching(false);
    }
  };

  const title = isIcon ? 'Change icon' : 'Change shape';
  const hint = isIcon
    ? 'Pick a new icon — its color and effects stay.'
    : 'Pick a new outline — the color, outline and effects stay.';

  const shapes = useMemo(() => SHAPE_SWATCHES, []);

  return createPortal(
    <div
      className="cce-swapmodal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="cce-swapmodal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="cce-swapmodal-head">
          <div className="cce-swapmodal-titles">
            <h2 className="cce-swapmodal-title">{title}</h2>
            <span className="cce-swapmodal-hint">{hint}</span>
          </div>
          {isIcon && (
            <input
              type="search"
              className="cce-swapmodal-search"
              placeholder="Search icons (e.g. home, star, arrow)…"
              aria-label="Search icons"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
              autoFocus
            />
          )}
          <button type="button" className="cce-swapmodal-close" aria-label="Close" onClick={() => onClose?.()}>
            ×
          </button>
        </div>

        {isIcon ? (
          <>
            <div className="cce-swapmodal-tags">
              {ICON_QUICK_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="cce-swapmodal-tag"
                  disabled={isSearching}
                  onClick={() => { setQuery(tag); runSearch(tag); }}
                >
                  {tag}
                </button>
              ))}
            </div>
            <div className="cce-swapmodal-grid cce-swapmodal-grid-icons">
              {icons.map((name, i) => (
                <button
                  key={`${name}-${i}`}
                  type="button"
                  className={`cce-swapmodal-thumb${activeIcon === name ? ' active' : ''}`}
                  title={name}
                  aria-label={`Icon ${name}`}
                  onClick={() => onPick?.(name)}
                >
                  <img
                    src={`${API_BASE}/design/icons/svg/${name.replace(':', '/')}.svg?color=${encodeURIComponent(ICON_PREVIEW_COLOR)}`}
                    alt=""
                    loading="lazy"
                    draggable={false}
                    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                  />
                </button>
              ))}
              {!icons.length && (
                <div className="cce-swapmodal-empty">
                  {isSearching ? 'Searching…'
                    : searched ? `No icons match “${query.trim()}”.`
                      : 'Search for an icon or tap a suggestion above.'}
                </div>
              )}
            </div>
            <div className="cce-swapmodal-credit">
              Icons by <a href="https://iconify.design" target="_blank" rel="noopener noreferrer">Iconify</a>
            </div>
          </>
        ) : (
          <div className="cce-swapmodal-grid cce-swapmodal-grid-shapes">
            {shapes.map((s) => (
              <button
                key={s.type}
                type="button"
                className="cce-swapmodal-thumb"
                title={s.label}
                aria-label={`Change to ${s.label}`}
                onClick={() => onPick?.(s.type)}
              >
                <svg width="40" height="40" viewBox="0 0 40 40" fill="currentColor" aria-hidden="true">
                  {s.el}
                </svg>
                <span className="cce-swapmodal-thumb-label">{s.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
